import { Fragment, useState } from 'react';
import { ButtonView } from '@vivli/shared/views';
import { Styles } from '@vivli/shared/theme';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { ApprovalStatusEnum, DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { finalize, first } from 'rxjs/operators';
import { GetOrgIdResult } from '@vivli/features/data-requests/infrastructure/interface';
import { DTICommonConst } from '@vivli/shared/infrastructure/constants';
import {
    useDataPackageApiContext,
    useDataRequestPermissionsContext,
    useDataRequestsApiContext,
    useModalApiContext
} from '@vivli/core/infrastructure/context';

interface ApproveButtonViewProps {
    getOrgId: (orgIds: string[]) => Promise<GetOrgIdResult>;
}

export const ApproveButtonView = ({ getOrgId }: ApproveButtonViewProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { dataRequest, isUpdatingStatus, setIsUpdatingStatus, updateDataRequest } = useDataRequestContext();
    const { getAllowedTransitions } = useDataRequestPermissionsContext();
    const modalService = useModalApiContext();
    const dataRequestsService = useDataRequestsApiContext();

    const approveText =
        dataRequest.status !== DataRequestStatusEnum.AwaitingDUAValidation &&
        dataRequest.status !== DataRequestStatusEnum.DUAReturnedToRequester
            ? 'Process Request'
            : 'Set DUA to Validated';

    const handleFinally = () => setLoadingStatus(false);
    const dataPackageService = useDataPackageApiContext();
    const handleError = (e) => {
        handleFinally();
    };

    const setLoadingStatus = (status: boolean) => {
        setIsLoading(status);
        setIsUpdatingStatus(status);
    };

    const handleApproveSubmission = (orgId: string, state: DataRequestStatusEnum) => {
        const message = (
            <Fragment>
                <p>If you wish, you may provide context for your processed request here. Simply click "OK" to continue without comment.</p>
                {(dataRequest.status === DataRequestStatusEnum.AwaitingDataProviderApproval ||
                    dataRequest.status === DataRequestStatusEnum.AwaitingIRPApproval) && (
                    <p>Include any feedback for the researcher in the chat.</p>
                )}
            </Fragment>
        );

        modalService.confirm(message, {
            title: 'Process Request Comment',
            promptLabel: 'Comment',
            showTextAreaPrompt: true,
            confirmText: 'Ok',
            cancelText: 'Cancel',
            onConfirm: (result) => {
                if (!result) {
                    handleFinally();
                    return;
                }

                setLoadingStatus(true);

                if (
                    dataRequest.status === DataRequestStatusEnum.AwaitingDataProviderApproval ||
                    dataRequest.status === DataRequestStatusEnum.AwaitingIRPApproval
                ) {
                    dataRequestsService
                        .approveRequest(dataRequest.id, result.comment, orgId, [], ApprovalStatusEnum.Approved)
                        .pipe(first(), finalize(handleFinally))
                        .subscribe(updateDataRequest, modalService.error);
                } else {
                    dataRequestsService
                        .setDataRequestStatus(dataRequest.id, state, result.comment)
                        .pipe(first(), finalize(handleFinally))
                        .subscribe(updateDataRequest, (error) => dataPackageService.handleSubmitErrorWithFileCheck(error, false, ''));
                }
            }
        });
    };

    const handleOnApprove = () => {
        const nextAllowedStatus: DataRequestStatusEnum = getAllowedTransitions(dataRequest).approve;

        if (
            nextAllowedStatus !== null ||
            dataRequest.status === DataRequestStatusEnum.AwaitingDataProviderApproval ||
            dataRequest.status === DataRequestStatusEnum.AwaitingIRPApproval
        ) {
            setLoadingStatus(true);

            if (
                dataRequest.status === DataRequestStatusEnum.SubmittedToVivli ||
                dataRequest.status === DataRequestStatusEnum.Approved ||
                dataRequest.status === DataRequestStatusEnum.AwaitingDUAValidation
            ) {
                handleApproveSubmission(null, nextAllowedStatus);
            } else {
                // we only need to worry about approval orgs for DataproviderApproval &&
                // IRPApproval stages

                dataRequestsService
                    .getApprovalOrgs(dataRequest.id)
                    .pipe(first())
                    .subscribe((orgIds) => {
                        getOrgId(orgIds)
                            .then(({ orgId }) => {
                                return handleApproveSubmission(orgId, nextAllowedStatus);
                            })
                            .finally(handleFinally);
                    }, handleError);
            }
        }
    };

    return (
        <ButtonView
            style={Styles.Button.BUTTON_DARKER_GREEN_CHECK}
            onClick={handleOnApprove}
            isLoading={isLoading}
            disabled={isUpdatingStatus}
            className="approveReject_approve"
            dataId={DTICommonConst.ApproveButton}
            theme={'darkgreen-on-white'}
        >
            {approveText}
        </ButtonView>
    );
};
